import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Loader,
  Center,
  SimpleGrid,
  Avatar,
  Badge,
  Box,
  ThemeIcon,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Select,
  Divider
} from '@mantine/core';
import { wishListsAPI, productsAPI } from '../services/api';

const WishListsPage = () => {
  const [wishLists, setWishLists] = useState([]);
  const [sharedWishLists, setSharedWishLists] = useState([]);
  const [publicWishLists, setPublicWishLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddItem, setShowAddItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('my-lists');

  const [createFormData, setCreateFormData] = useState({
    name: '',
    description: '',
    is_public: false
  });

  const [addItemForm, setAddItemForm] = useState({
    product_name: '',
    product_url: '',
    price: '',
    notes: ''
  });

  useEffect(() => {
    loadWishListsData();
  }, []);

  const loadWishListsData = async () => {
    try {
      const [myListsRes, sharedRes, publicRes] = await Promise.all([
        wishListsAPI.getAll(),
        wishListsAPI.getShared(),
        wishListsAPI.getPublic({ limit: 8 })
      ]);

      setWishLists(myListsRes.data || []);
      setSharedWishLists(sharedRes.data || []);
      setPublicWishLists(publicRes.data || []);
    } catch (error) {
      console.error('Failed to load wish lists data:', error);
      setWishLists([]);
      setSharedWishLists([]);
      setPublicWishLists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWishList = async () => {
    if (!createFormData.name.trim()) {
      alert('Wish list name is required');
      return;
    }

    try {
      await wishListsAPI.create(createFormData);
      setCreateFormData({ name: '', description: '', is_public: false });
      setShowCreateForm(false);
      loadWishListsData();
    } catch (error) {
      console.error('Failed to create wish list:', error);
      alert('Failed to create wish list. Please try again.');
    }
  };

  const handleAddItem = async () => {
    if (!showAddItem || !addItemForm.product_name.trim()) return;

    try {
      await wishListsAPI.addItem(showAddItem, addItemForm);
      setAddItemForm({ product_name: '', product_url: '', price: '', notes: '' });
      setShowAddItem(null);
      loadWishListsData();
    } catch (error) {
      console.error('Failed to add item:', error);
      alert('Failed to add item. Please try again.');
    }
  };

  const handleMarkPurchased = async (wishListId, itemId) => {
    try {
      await wishListsAPI.markPurchased(wishListId, itemId);
      loadWishListsData();
    } catch (error) {
      console.error('Failed to mark as purchased:', error);
    }
  };

  const handleRemoveItem = async (wishListId, itemId) => {
    try {
      await wishListsAPI.removeItem(wishListId, itemId);
      loadWishListsData();
    } catch (error) {
      console.error('Failed to remove item:', error);
    }
  };

  const handleDeleteWishList = async () => {
    if (!showDeleteConfirm) return;

    try {
      await wishListsAPI.delete(showDeleteConfirm);
      setShowDeleteConfirm(null);
      loadWishListsData();
    } catch (error) {
      console.error('Failed to delete wish list:', error);
    }
  };

  const getWishListName = (id) => {
    const wishList = wishLists.find(w => w.id === id);
    return wishList ? wishList.name : 'this wish list';
  };

  if (loading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading your wish lists...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Enhanced Header */}
        <Box mb="xl" py="lg">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title
                order={1}
                size="h1"
                fw={700}
                style={{
                  background: 'linear-gradient(45deg, #1971c2 0%, #15aabf 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}
              >
                🎁 Wish Lists
              </Title>
              <Text size="xl" c="dimmed" fw={400}>
                Create and manage your gift wish lists, share with friends and family
              </Text>
            </Box>
            <Button
              size="lg"
              variant="gradient"
              gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
              onClick={() => setShowCreateForm(true)}
              style={{ boxShadow: '0 4px 12px rgba(25,113,194,0.3)' }}
            >
              ✨ Create Wish List
            </Button>
          </Group>
        </Box>

        {/* Tabs for different wish list views */}
        <Box mb="xl">
          <Group gap="xs" mb="lg">
            {[
              { value: 'my-lists', label: 'My Lists', count: wishLists.length },
              { value: 'shared', label: 'Shared With Me', count: sharedWishLists.length },
              { value: 'public', label: 'Public Lists', count: publicWishLists.length }
            ].map(tab => (
              <Button
                key={tab.value}
                variant={activeTab === tab.value ? 'filled' : 'light'}
                color="blue"
                onClick={() => setActiveTab(tab.value)}
                rightSection={tab.count > 0 && <Badge size="xs" color="blue">{tab.count}</Badge>}
              >
                {tab.label}
              </Button>
            ))}
          </Group>

          {/* My Lists Tab */}
          {activeTab === 'my-lists' && (
            <Box>
              {wishLists.length === 0 ? (
                <Card
                  p="xl"
                  radius="lg"
                  withBorder
                  style={{
                    background: 'linear-gradient(135deg, rgba(25,113,194,0.05) 0%, rgba(0,0,0,0) 100%)',
                    borderStyle: 'dashed',
                    borderWidth: '2px'
                  }}
                >
                  <Center py="xl">
                    <Stack align="center" gap="md">
                      <ThemeIcon size={80} radius="xl" variant="light" color="blue">
                        <Text size="40px">🎁</Text>
                      </ThemeIcon>
                      <Title order={3} ta="center">
                        No wish lists yet
                      </Title>
                      <Text c="dimmed" ta="center" maw={400}>
                        Create your first wish list to start collecting items you'd love to receive as gifts
                      </Text>
                      <Button
                        size="lg"
                        variant="gradient"
                        gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                        onClick={() => setShowCreateForm(true)}
                        mt="md"
                      >
                        Create Your First Wish List
                      </Button>
                    </Stack>
                  </Center>
                </Card>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                  {wishLists.map(wishList => (
                    <Card
                      key={wishList.id}
                      p="lg"
                      radius="lg"
                      withBorder
                      shadow="sm"
                      style={{
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      <Group justify="space-between" mb="md">
                        <Box>
                          <Text fw={700} size="lg">
                            {wishList.name}
                          </Text>
                          {wishList.is_public && (
                            <Badge size="xs" color="green" variant="light">
                              Public
                            </Badge>
                          )}
                        </Box>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => setShowDeleteConfirm(wishList.id)}
                        >
                          🗑️
                        </ActionIcon>
                      </Group>

                      {wishList.description && (
                        <Text c="dimmed" size="sm" mb="md">
                          {wishList.description}
                        </Text>
                      )}

                      <Group justify="space-between" mb="md">
                        <Text size="sm" c="dimmed">
                          {wishList.items_count || 0} items
                        </Text>
                        <Text size="sm" c="dimmed">
                          Created {new Date(wishList.created_at).toLocaleDateString()}
                        </Text>
                      </Group>

                      <Divider my="sm" />

                      <Group grow>
                        <Button
                          variant="light"
                          color="blue"
                          size="sm"
                          onClick={() => setShowAddItem(wishList.id)}
                        >
                          ➕ Add Item
                        </Button>
                        <Button variant="light" color="teal" size="sm">
                          👁️ View Items
                        </Button>
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          )}

          {/* Shared Lists Tab */}
          {activeTab === 'shared' && (
            <Box>
              {sharedWishLists.length === 0 ? (
                <Center py="xl">
                  <Stack align="center" gap="md">
                    <ThemeIcon size={60} radius="xl" variant="light" color="teal">
                      <Text size="32px">🤝</Text>
                    </ThemeIcon>
                    <Text c="dimmed">No wish lists shared with you yet</Text>
                  </Stack>
                </Center>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                  {sharedWishLists.map(wishList => (
                    <Card
                      key={wishList.id}
                      p="lg"
                      radius="lg"
                      withBorder
                      shadow="sm"
                      style={{
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                      }}
                    >
                      <Group justify="space-between" mb="md">
                        <Box>
                          <Text fw={700} size="lg">
                            {wishList.name}
                          </Text>
                          <Text size="sm" c="dimmed">
                            by {wishList.owner_name}
                          </Text>
                        </Box>
                      </Group>

                      {wishList.description && (
                        <Text c="dimmed" size="sm" mb="md">
                          {wishList.description}
                        </Text>
                      )}

                      <Button variant="light" color="teal" fullWidth>
                        👁️ View Items
                      </Button>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          )}

          {/* Public Lists Tab */}
          {activeTab === 'public' && (
            <Box>
              {publicWishLists.length === 0 ? (
                <Center py="xl">
                  <Stack align="center" gap="md">
                    <ThemeIcon size={60} radius="xl" variant="light" color="grape">
                      <Text size="32px">🌍</Text>
                    </ThemeIcon>
                    <Text c="dimmed">No public wish lists available</Text>
                  </Stack>
                </Center>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
                  {publicWishLists.map(wishList => (
                    <Card
                      key={wishList.id}
                      p="lg"
                      radius="lg"
                      withBorder
                      shadow="sm"
                      style={{
                        transition: 'all 0.3s ease',
                        background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
                      }}
                    >
                      <Group justify="space-between" mb="md">
                        <Box>
                          <Text fw={700} size="lg">
                            {wishList.name}
                          </Text>
                          <Text size="sm" c="dimmed">
                            by {wishList.owner_name}
                          </Text>
                        </Box>
                      </Group>

                      {wishList.description && (
                        <Text c="dimmed" size="sm" mb="md">
                          {wishList.description}
                        </Text>
                      )}

                      <Button variant="light" color="grape" fullWidth>
                        👁️ View Items
                      </Button>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          )}
        </Box>

        {/* Create Wish List Modal */}
        <Modal
          opened={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          title={
            <Text size="xl" fw={700}>
              ✨ Create New Wish List
            </Text>
          }
          size="md"
          centered
        >
          <Stack gap="md">
            <TextInput
              label="Wish List Name"
              placeholder="e.g., Birthday Wishes, Tech Gadgets"
              value={createFormData.name}
              onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
              size="md"
              required
            />

            <Textarea
              label="Description (optional)"
              placeholder="What kind of gifts are you hoping for?"
              value={createFormData.description}
              onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
              rows={3}
              size="md"
            />

            <Group grow>
              <Button
                size="lg"
                variant="gradient"
                gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                onClick={handleCreateWishList}
              >
                Create Wish List
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Add Item Modal */}
        <Modal
          opened={!!showAddItem}
          onClose={() => setShowAddItem(null)}
          title={
            <Text size="xl" fw={700}>
              ➕ Add Wish List Item
            </Text>
          }
          size="md"
          centered
        >
          <Stack gap="md">
            <TextInput
              label="Item Name"
              placeholder="What would you like?"
              value={addItemForm.product_name}
              onChange={(e) => setAddItemForm({ ...addItemForm, product_name: e.target.value })}
              size="md"
              required
            />

            <TextInput
              label="Product URL (optional)"
              placeholder="Link to the product"
              value={addItemForm.product_url}
              onChange={(e) => setAddItemForm({ ...addItemForm, product_url: e.target.value })}
              size="md"
            />

            <TextInput
              label="Price (optional)"
              placeholder="Approximate price"
              value={addItemForm.price}
              onChange={(e) => setAddItemForm({ ...addItemForm, price: e.target.value })}
              size="md"
            />

            <Textarea
              label="Notes (optional)"
              placeholder="Any specific details or preferences?"
              value={addItemForm.notes}
              onChange={(e) => setAddItemForm({ ...addItemForm, notes: e.target.value })}
              rows={2}
              size="md"
            />

            <Group grow>
              <Button
                size="lg"
                variant="gradient"
                gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                onClick={handleAddItem}
              >
                Add Item
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => setShowAddItem(null)}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={!!showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(null)}
          title={
            <Text size="xl" fw={700}>
              🗑️ Delete Wish List
            </Text>
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to delete <strong>"{getWishListName(showDeleteConfirm)}"</strong>?
              This action cannot be undone.
            </Text>

            <Group grow>
              <Button
                color="red"
                onClick={handleDeleteWishList}
              >
                Delete Wish List
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </Box>
  );
};

export default WishListsPage;
